import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { toast } from "sonner";
import { Lock, X, Check, AlertTriangle, Skull, ShieldCheck } from "lucide-react";
import TeamBadge from "@/components/app/TeamBadge";

const bucketLabelOf = (m, isLeague) => (m.matchday != null && m.stage === "group" ? `${isLeague ? "GW" : "MD"} ${m.matchday}` : (m.stage || "").toUpperCase());
const bucketKeyOf = (m) => (m.matchday != null && m.stage === "group" ? `md:${m.matchday}` : `stage:${m.stage}`);

// Knockout rounds require fewer survivor picks than the group stage.
const KO_PICKS = { r32: 3, r16: 2, qf: 2, sf: 1, final: 1 };
const requiredFor = (m0, n, matchCount) => {
  const desired = m0?.stage === "group" ? n : (KO_PICKS[(m0?.stage || "").toLowerCase()] ?? n);
  return Math.min(desired, matchCount);
};

const resultStyle = {
  survived: "text-green-400",
  strike: "text-primary-accent line-through",
  pending: "text-zinc-500",
};

export default function Survivor() {
  const { active } = useTournament();
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState({});
  const [me, setMe] = useState({ picks: [], strikes: 0, strikes_allowed: 2, picks_per_matchday: 3, alive: true });
  const [drafts, setDrafts] = useState({}); // bucketKey -> { [match_id]: team_id }
  const [busy, setBusy] = useState(false);

  const picks = me.picks || [];
  const N = me.picks_per_matchday || 3;
  const strikesAllowed = me.strikes_allowed || 2;
  const isLeague = me.mode === "weekly_points";
  const survivePoints = me.survive_points ?? 7;
  const weeklyAllowed = me.weekly_strikes_allowed ?? 1;
  const weeklyMap = useMemo(
    () => Object.fromEntries((me.weekly || []).map((w) => [w.bucket_key, w])),
    [me.weekly]
  );

  const load = async () => {
    if (!active) return;
    const tid = active.tournament_id;
    const [mR, tR, pR] = await Promise.all([
      api.get(`/tournaments/${tid}/matches`),
      api.get(`/tournaments/${tid}/teams`),
      api.get(`/tournaments/${tid}/survivor/me`),
    ]);
    setMatches(mR.data);
    setTeams(Object.fromEntries(tR.data.map((t) => [t.team_id, t])));
    setMe(pR.data);
    // seed drafts from existing pending picks per bucket
    const seed = {};
    pR.data.picks.forEach((p) => {
      const m = mR.data.find((x) => x.match_id === p.match_id);
      if (m && p.result === "pending") {
        const k = bucketKeyOf(m);
        (seed[k] = seed[k] || {})[p.match_id] = p.team_id;
      }
    });
    setDrafts(seed);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.tournament_id]);

  // teams used anywhere (any bucket) — for no-reuse enforcement (elimination mode only;
  // leagues allow reusing teams across gameweeks)
  const usedTeamIds = useMemo(
    () => (isLeague ? new Set() : new Set(picks.map((p) => p.team_id))),
    [picks, isLeague]
  );

  // server picks grouped by bucket (for the "your picks" + saved-state checks)
  const serverByBucket = useMemo(() => {
    const map = {};
    picks.forEach((p) => {
      const m = matches.find((x) => x.match_id === p.match_id);
      if (m) (map[bucketKeyOf(m)] = map[bucketKeyOf(m)] || []).push(p);
    });
    return map;
  }, [picks, matches]);

  // open (still-pickable) buckets only
  const openBuckets = useMemo(() => {
    const now = new Date();
    const liveMDs = active?.config?.live_matchdays || [1];
    const isFinalised = (m) => {
      const h = teams[m.home_team_id], a = teams[m.away_team_id];
      return h && h.code !== "TBD" && a && a.code !== "TBD";
    };
    const buckets = {};
    matches.forEach((m) => {
      const k = bucketKeyOf(m);
      (buckets[k] = buckets[k] || { key: k, label: bucketLabelOf(m, isLeague), matches: [] }).matches.push(m);
    });
    return Object.values(buckets)
      .map((b) => {
        const deadline = new Date(Math.min(...b.matches.map((m) => new Date(m.kickoff).getTime())));
        return { ...b, deadline, locked: deadline <= now, required: requiredFor(b.matches[0], N, b.matches.length) };
      })
      .filter((b) => {
        if (b.locked) return false;
        const m0 = b.matches[0];
        // group matchdays must be admin-enabled; knockout rounds must have decided teams
        if (m0.stage === "group" && m0.matchday != null) return liveMDs.includes(m0.matchday);
        return b.matches.every((m) => isFinalised(m));
      })
      .sort((a, b) => a.deadline - b.deadline);
  }, [matches, N, active, teams]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const toggleDraft = (bucketKey, required, match_id, team_id) => {
    if (usedTeamIds.has(team_id) && !(drafts[bucketKey] || {})[match_id]) {
      // team used in another bucket already (and not the current selection for this match)
      const usedHere = picks.find((p) => p.team_id === team_id);
      const usedM = usedHere && matches.find((x) => x.match_id === usedHere.match_id);
      if (!(usedM && bucketKeyOf(usedM) === bucketKey)) {
        toast.error("That team is already used in another matchday");
        return;
      }
    }
    setDrafts((prev) => {
      const cur = { ...(prev[bucketKey] || {}) };
      if (cur[match_id] === team_id) {
        delete cur[match_id]; // deselect
      } else {
        if (!cur[match_id] && Object.keys(cur).length >= required) {
          toast.error(`You can only pick ${required} team${required !== 1 ? "s" : ""} this matchday`);
          return prev;
        }
        cur[match_id] = team_id; // select / replace within this match
      }
      return { ...prev, [bucketKey]: cur };
    });
  };

  const saveBucket = async (bucket) => {
    const draft = drafts[bucket.key] || {};
    const list = Object.entries(draft).map(([match_id, team_id]) => ({ match_id, team_id }));
    if (list.length !== bucket.required) {
      toast.error(`Pick exactly ${bucket.required} team${bucket.required !== 1 ? "s" : ""} to lock in ${bucket.label}`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/tournaments/${active.tournament_id}/survivor/matchday`, { picks: list });
      await load();
      toast.success(`${bucket.label} picks locked in`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const clearBucket = async (bucket) => {
    const anyMatch = bucket.matches[0]?.match_id;
    setBusy(true);
    try {
      await api.delete(`/tournaments/${active.tournament_id}/survivor/matchday/${anyMatch}`);
      await load();
      toast.success(`${bucket.label} picks cleared`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!active) return null;

  const alive = me.alive;
  const strikes = me.strikes || 0;
  const showPicker = isLeague ? true : alive;

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-tag mb-1">{isLeague ? "Weekly Survivor" : "Last Players Standing"}</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase">Survivor Challenge</h1>
        </div>
        {isLeague ? (
          <div className="flex items-center gap-3">
            <div className="surface-card px-4 py-2" data-testid="survivor-total-points">
              <span className="label-tag">Survivor Points</span>
              <div className="font-display text-3xl text-primary-accent tabular-nums">{me.total_points ?? 0}</div>
            </div>
            <div className="surface-card px-4 py-2" data-testid="survivor-weeks-survived">
              <span className="label-tag">Weeks Survived</span>
              <div className="font-display text-3xl tabular-nums">
                {(me.weekly || []).filter((w) => w.survived).length}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            {/* Strikes meter */}
            <div className="surface-card px-4 py-2" data-testid="survivor-strikes">
              <span className="label-tag">Strikes</span>
              <div className="flex items-center gap-1.5 mt-1">
                {Array.from({ length: strikesAllowed }).map((_, i) => (
                  <span key={i} className={`w-5 h-5 flex items-center justify-center border ${i < strikes ? "bg-primary-accent border-primary-accent text-white" : "border-zinc-700 text-zinc-700"}`}>
                    <X size={12} />
                  </span>
                ))}
                <span className="font-display text-lg ml-1 tabular-nums">{strikes}/{strikesAllowed}</span>
              </div>
            </div>
            {/* Status */}
            <div className={`surface-card px-4 py-2 ${!alive ? "accent-bar" : ""}`} data-testid="survivor-status">
              <span className="label-tag">Status</span>
              <div className={`font-display text-2xl flex items-center gap-2 ${alive ? "text-primary-accent" : "text-zinc-500 line-through"}`}>
                {alive ? <><ShieldCheck size={20} /> ALIVE</> : <><Skull size={20} /> ELIMINATED</>}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="surface-card p-4 mb-6">
        <div className="label-tag mb-2">How Survivor Works</div>
        {isLeague ? (
          <ul className="text-sm text-zinc-400 space-y-1 list-disc list-inside">
            <li>Pick <span className="text-white font-display">{N} team{N !== 1 ? "s" : ""}</span> every gameweek (one team per match).</li>
            <li>A picked team that <span className="text-white">draws or loses</span> is a <span className="text-primary-accent font-display">strike</span>.</li>
            <li>Up to <span className="text-white font-display">{weeklyAllowed} strike{weeklyAllowed !== 1 ? "s" : ""}</span> and you still <span className="text-green-400">survive</span> the week for <span className="text-white font-display">+{survivePoints} pts</span>. More strikes = <span className="text-primary-accent">struck out</span> (0 pts that week).</li>
            <li>No eliminations — it runs all season. Teams <span className="text-white">can be reused</span> across gameweeks.</li>
            <li>Most Survivor points across the season wins.</li>
          </ul>
        ) : (
          <ul className="text-sm text-zinc-400 space-y-1 list-disc list-inside">
            <li>Pick <span className="text-white font-display">{N} team{N !== 1 ? "s" : ""}</span> every group matchday (one per match) — knockouts need fewer (R32 3, R16/QF 2, SF/Final 1). Lock them all in before the deadline.</li>
            <li>A picked team that <span className="text-white">draws or loses</span> is a <span className="text-primary-accent font-display">strike</span>. <span className="text-white">Missing a pick</span> for a locked matchday also counts as a strike — and <span className="text-primary-accent">skipping a matchday entirely eliminates you</span>.</li>
            <li>Collect <span className="text-white font-display">{strikesAllowed} strike{strikesAllowed !== 1 ? "s" : ""}</span> and you are <span className="text-primary-accent">eliminated</span> — strikes carry across the whole tournament.</li>
            <li>A team <span className="text-white">cannot be reused</span> anywhere in the tournament. Change picks freely until the matchday locks.</li>
            <li>Last players standing (fewest strikes, most correct picks) take the crown.</li>
          </ul>
        )}
      </div>

      {/* YOUR PICKS (settled + pending) grouped by bucket */}
      <h2 className="font-display text-2xl uppercase mb-3">Your Picks</h2>
      {picks.length === 0 && <div className="text-zinc-500 text-sm mb-8">No picks yet — lock in your first matchday below.</div>}
      {picks.length > 0 && (
        <div className="space-y-3 mb-8">
          {Object.entries(serverByBucket).map(([k, ps]) => {
            const m0 = matches.find((x) => bucketKeyOf(x) === k);
            return (
              <div key={k} className="surface-card p-3" data-testid={`mypicks-bucket-${k}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="label-tag">{m0 ? bucketLabelOf(m0, isLeague) : k}</div>
                  {isLeague && weeklyMap[k] && weeklyMap[k].survived !== null && (
                    <span className={`text-xs uppercase tracking-widest font-display ${weeklyMap[k].survived ? "text-green-400" : "text-primary-accent"}`} data-testid={`week-result-${k}`}>
                      {weeklyMap[k].survived ? `Survived +${weeklyMap[k].points}` : "Struck out · 0"}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {ps.map((p) => {
                    const t = teams[p.team_id];
                    const m = matches.find((x) => x.match_id === p.match_id);
                    return (
                      <div key={p.match_id} className={`border px-3 py-2 flex items-center gap-2 ${
                        p.result === "strike" ? "border-primary-accent/60" : p.result === "survived" ? "border-green-500/50" : "border-zinc-800"
                      }`} data-testid={`my-pick-${p.match_id}`}>
                        <TeamBadge team={t} size="md"/>
                        <div className="text-xs uppercase tracking-widest flex flex-col gap-0.5">
                          {m && m.status === "completed" ? (
                            <span className={resultStyle[p.result]} data-testid={`pick-result-${p.match_id}`}>
                              {m.home_score}-{m.away_score} · {p.result === "survived" ? "Won" : p.result === "strike" ? "Strike" : p.result}
                            </span>
                          ) : (
                            <span className="text-zinc-600">Awaiting kickoff</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isLeague && !alive && (
        <div className="surface-card p-4 mb-6 border border-primary-accent/40 flex items-center gap-3" data-testid="eliminated-banner">
          <Skull size={20} className="text-primary-accent" />
          <span className="text-sm text-zinc-300">You have been eliminated after {strikes} strikes. You can still watch the leaderboard play out.</span>
        </div>
      )}

      {showPicker && (
        <>
          <h2 className="font-display text-2xl uppercase mb-3">{isLeague ? "Pick By Gameweek" : "Pick By Matchday"}</h2>
          {openBuckets.length === 0 && <div className="text-zinc-500 text-sm">No upcoming {isLeague ? "gameweeks" : "matchdays"} open for picks.</div>}
          <div className="space-y-6">
            {openBuckets.map((bucket) => {
              const draft = drafts[bucket.key] || {};
              const draftCount = Object.keys(draft).length;
              const serverPicks = serverByBucket[bucket.key] || [];
              const draftMatchesServer =
                serverPicks.length === draftCount &&
                serverPicks.every((p) => draft[p.match_id] === p.team_id);
              const isSaved = serverPicks.length > 0 && draftMatchesServer && draftCount === bucket.required;

              return (
                <section key={bucket.key} className="surface-card p-4" data-testid={`bucket-${bucket.key}`}>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-xl uppercase tracking-wider text-primary-accent">{bucket.label}</h3>
                      <span className="text-xs text-zinc-500 uppercase tracking-widest">
                        {bucket.matches.length} matches · pick {bucket.required}
                      </span>
                      <span className="text-xs text-zinc-500 uppercase tracking-widest flex items-center gap-1" data-testid={`deadline-${bucket.key}`}>
                        <Lock size={10} /> Locks {bucket.deadline.toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs uppercase tracking-widest font-display ${draftCount === bucket.required ? "text-green-400" : "text-zinc-400"}`} data-testid={`count-${bucket.key}`}>
                        {draftCount}/{bucket.required} selected
                      </span>
                      {isSaved ? (
                        <span className="text-xs uppercase tracking-widest text-green-400 flex items-center gap-1" data-testid={`saved-${bucket.key}`}>
                          <Check size={12}/> Saved
                        </span>
                      ) : (
                        <button
                          data-testid={`save-${bucket.key}`}
                          disabled={busy || draftCount !== bucket.required}
                          onClick={() => saveBucket(bucket)}
                          className="text-xs uppercase tracking-widest bg-primary-accent text-white px-3 py-1.5 flex items-center gap-1 hover:bg-[var(--primary-hover)] disabled:opacity-40"
                        >
                          <Check size={12}/> Lock In
                        </button>
                      )}
                      {(serverPicks.length > 0 || draftCount > 0) && (
                        <button
                          data-testid={`clear-${bucket.key}`}
                          disabled={busy}
                          onClick={() => clearBucket(bucket)}
                          className="text-xs uppercase tracking-widest text-zinc-400 hover:text-primary-accent flex items-center gap-1 border border-zinc-700 px-2 py-1.5 hover:border-primary-accent"
                        >
                          <X size={12}/> Clear
                        </button>
                      )}
                    </div>
                  </div>

                  {draftCount > bucket.required && (
                    <div className="text-xs text-primary-accent flex items-center gap-1 mb-2"><AlertTriangle size={12}/> Too many selected</div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {bucket.matches.map((m) => {
                      const h = teams[m.home_team_id];
                      const a = teams[m.away_team_id];
                      const selectedTeam = draft[m.match_id];
                      return (
                        <div key={m.match_id} className="border border-zinc-800 p-3" data-testid={`survivor-match-${m.match_id}`}>
                          <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">
                            {new Date(m.kickoff).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {[h, a].map((t) => {
                              if (!t) return null;
                              const selected = selectedTeam === t.team_id;
                              // used in ANOTHER bucket (not selectable)
                              const usedHere = picks.find((p) => p.team_id === t.team_id);
                              const usedM = usedHere && matches.find((x) => x.match_id === usedHere.match_id);
                              const usedElsewhere = usedHere && !(usedM && bucketKeyOf(usedM) === bucket.key);
                              const disabled = usedElsewhere && !selected;
                              return (
                                <button
                                  key={t.team_id}
                                  disabled={disabled}
                                  data-testid={`pick-${m.match_id}-${t.team_id}`}
                                  onClick={() => toggleDraft(bucket.key, bucket.required, m.match_id, t.team_id)}
                                  className={`p-3 border text-left transition-colors ${
                                    selected ? "bg-primary-accent border-primary-accent text-white" :
                                    disabled ? "border-zinc-900 text-zinc-700 cursor-not-allowed" :
                                    "border-zinc-800 hover:border-primary-accent"
                                  }`}
                                  title={usedElsewhere ? "Team already used in another matchday" : ""}
                                >
                                  <TeamBadge team={t} size="md"/>
                                  {usedElsewhere && !selected && <div className="text-[10px] flex items-center gap-1 mt-1 text-zinc-600"><Lock size={10}/> Used elsewhere</div>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
